import React, { useState, useEffect } from "react";
import {
  Box,
  VStack,
  Heading,
  Button,
  Text,
  HStack,
  Badge,
  useColorModeValue,
  SimpleGrid,
  Switch,
  Spinner,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthProvider";
import { getAllPlans } from "../../services/planService";
import { useTranslation } from "react-i18next";

const PricingPlans = ({ currentPlan, showNoSubscriptionMessage = false }) => {
  const { t, i18n } = useTranslation();

  const textColor = useColorModeValue("gray.700", "white");
  const cardBgColor = useColorModeValue("white", "gray.700");
  const accentColor = useColorModeValue("blue.500", "blue.300");
  const highlightColor = useColorModeValue("yellow.400", "yellow.300");

  const { user, firebaseUser } = useAuth();
  const [isYearly, setIsYearly] = useState(false);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const plansData = await getAllPlans();
        setPlans(plansData);
      } catch (error) {
        console.error(t("pricingplans.fetch_error"), error);
      } finally {
        setLoading(false);
      }
    };
    fetchPlans();
  }, [t]);

  const handleSubscribe = async (plan, billingCycle) => {
    try {
      if (!firebaseUser) {
        console.warn("User is not authenticated. Redirecting to sign-in.");
        navigate("/auth/sign-up", { state: { redirectTo: "/personal/billing", plan } });
        return;
      }

      const token = await firebaseUser.getIdToken();
      const stripeNameId = billingCycle.stripeNameId.replace(/ /g, "-").toLowerCase();
      const subscribeUrl = `${import.meta.env.VITE_API_APP_BACKEND_URL}/api/subscribe?plan=${stripeNameId}`;

      const response = await fetch(subscribeUrl, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        const { url } = await response.json();
        window.location.href = url;
      } else {
        const errorData = await response.json();
        console.error(t("pricingplans.subscribe_error"), response.status, errorData?.message || response.statusText);
      }
    } catch (error) {
      console.error(t("pricingplans.error_initiating_subscription"), error);
    }
  };

  if (loading) {
    return (
      <Box textAlign="center" py={8}>
        <Spinner size="lg" />
        <Text mt={4}>{t("pricingplans.loading_message")}</Text>
      </Box>
    );
  }

  return (
    <Box px={8}>
      {showNoSubscriptionMessage && currentPlan === null && (
        <Text textAlign="center" fontSize="lg" mb={6} color="red.500">
          {t("pricingplans.no_subscription_message")}
        </Text>
      )}

      <HStack justify="center" mb={16}>
        <Text fontSize="sm">{t("pricingplans.billing_monthly")}</Text>
        <Switch
          size="md"
          isChecked={isYearly}
          onChange={(e) => setIsYearly(e.target.checked)}
          colorScheme="teal"
          ml={2}
          mr={2}
          sx={{
            '--switch-bg': 'var(--green-color)',
            background: 'var(--switch-bg)',
          }}
        />
        <Text fontSize="sm">{t("pricingplans.billing_yearly")}{" "}{t("pricingplans.(Save_20%)")}</Text>
      </HStack>

      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={10} maxW="1200px" mx="auto">
        {plans.map((plan) => {
          const planKey = plan.name.replace(/\s+/g, '_').toLowerCase(); // Replace spaces with underscores
          const billingCycle = plan.billingCycles.find((cycle) =>
            isYearly ? cycle.cycle === "yearly" : cycle.cycle === "monthly"
          );

          if (!billingCycle) return null;

          return (
            <Box
              key={plan.id}
              bg={cardBgColor}
              p={6}
              borderRadius="lg"
              shadow="md"
              textAlign="center"
              borderWidth="1px"
              border={plan.popular ? `2px solid ${accentColor}` : "1px solid gray"}
              _hover={{ transform: "scale(1.05)", shadow: "xl" }}
            >
              {plan.popular && (
                <Badge
                  bg={highlightColor}
                  color="black"
                  px={3}
                  py={1}
                  mb={10}
                  mt={-10}
                  borderRadius="full"
                  fontSize="xs"
                >
                  {t("pricingplans.most_popular")}
                </Badge>
              )}
              <VStack spacing={4}>
                <Heading size="md" color={textColor}>
                  {t(`pricingplans.${planKey}.name`, plan.name)}
                </Heading>
                <Text fontSize="2xl" fontWeight="bold" color="teal.600">
                  {new Intl.NumberFormat(i18n.language, {
                    style: 'currency',
                    currency: billingCycle.currency,
                  }).format(billingCycle.price)}{' '}
                  ({t(`pricingplans.billing_${billingCycle.cycle}`)})
                </Text>
                <VStack spacing={2} align="start">
                  {plan.features.map((feature, idx) => (
                    <HStack key={idx}>
                      <Badge colorScheme="teal">✓</Badge>
                      <Text color={textColor}>
                        {t(`pricingplans.${planKey}.features.${idx}`, feature)}
                      </Text>
                    </HStack>
                  ))}
                </VStack>
                <Text color={textColor} fontSize="md">
                  {t(`pricingplans.${planKey}.description`, plan.description)}
                </Text>
                <Text color={textColor} fontSize="sm" mt={2}>
                  {t(`pricingplans.${planKey}.targetAudience`, plan.targetAudience)}
                </Text>
                <Button
                  mt={4}
                  mb={6}
                  colorScheme={currentPlan?.planId === billingCycle?.stripePriceId ? "gray" : "teal"}
                  isDisabled={currentPlan?.planId === billingCycle?.stripePriceId}
                  size="lg"
                  onClick={() => handleSubscribe(plan, billingCycle)}
                >
                  {currentPlan?.planId === billingCycle?.stripePriceId
                    ? t("pricingplans.current_plan")
                    : t("pricingplans.select_plan")}
                </Button>
              </VStack>
            </Box>
          );
        })}
      </SimpleGrid>
    </Box>
  );
};

export default PricingPlans;
